if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this._avOnLoad = () => {
          this._avPreloadTimer = window.setTimeout(() => this.preloadVariantImages(), 200);
        };
        if (document.readyState === 'complete') this._avOnLoad();
        else window.addEventListener('load', this._avOnLoad, { once: true });
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        window.clearTimeout(this._avPreloadTimer);
        window.removeEventListener('load', this._avOnLoad);
        this._avMediaSequence = (this._avMediaSequence || 0) + 1;
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Show only media for an exact matching combination. Shopify still
        // confirms price, availability and purchase inputs through its request.
        this.previewVariantMedia(
          shouldSwapProduct ? null : this.variantForOptionValues(selectedOptionValues)
        );

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        const requestId = this._avRequestId = (this._avRequestId || 0) + 1;
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Unable to update product selection');
            return response.text();
          })
          .then((responseText) => {
            if (requestId !== this._avRequestId || !this.isConnected) return;
            this._avMediaSequence = (this._avMediaSequence || 0) + 1;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (requestId !== this._avRequestId || !this.isConnected) return;
            this.previewVariantMedia(this.getSelectedVariant(this));
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      variantForOptionValues(optionValues) {
        const variants = this.parseJsonScript(this.variantSelectors, '[data-artvint-variants]');
        const controls = Array.from(
          this.variantSelectors?.querySelectorAll('option[data-option-value-id], input[data-option-value-id]') || []
        );
        const values = optionValues.map((id) =>
          controls.find((control) => control.dataset.optionValueId === String(id))?.value
        );
        return Array.isArray(variants) ? variants.find((variant) =>
          variant.options.length === values.length &&
          variant.options.every((value, index) => value === values[index])
        ) : null;
      }

      variantMediaNode(variant) {
        const id = variant?.featured_media?.id;
        const gallery = this.querySelector('media-gallery');
        if (!id || !gallery) return null;
        const mediaId = `${this.dataset.section}-${id}`;
        const live = Array.from(gallery.querySelectorAll('li[data-media-id]'))
          .find((node) => node.dataset.mediaId === mediaId);
        if (live) return { gallery, node: live, mediaId, template: false };
        const template = Array.from(gallery.querySelectorAll('template[data-artvint-media-id]'))
          .find((node) => node.dataset.artvintMediaId === mediaId);
        const node = template?.content.querySelector('li[data-media-id]');
        return node ? { gallery, node, mediaId, template: true } : null;
      }

      warmVariantImage(variant, urgent = false) {
        const media = this.variantMediaNode(variant);
        const source = media?.node.querySelector('.product__media img') || media?.node.querySelector('img');
        if (!source) return Promise.resolve(false);
        this._avImageCache ||= new Map();
        const src = source.getAttribute('src');
        const srcset = source.getAttribute('srcset') || '';
        // `auto` is only valid for lazy loading; use the explicit fallback sizes.
        const sizes = (source.getAttribute('sizes') || '').replace(/^auto\s*,?\s*/i, '') || '100vw';
        const key = [src, srcset, sizes].join('|');
        const cached = this._avImageCache.get(key);
        if (cached) {
          if (urgent) cached.image.fetchPriority = 'high';
          return cached.promise;
        }
        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = urgent ? 'high' : 'low';
        image.sizes = sizes;
        if (srcset) image.srcset = srcset;
        image.src = src;
        const promise = image.decode().then(() => true).catch(() => {
          this._avImageCache.delete(key);
          return false;
        });
        this._avImageCache.set(key, { image, promise });
        return promise;
      }

      syncVariantThumbnail(mediaId, sourceHtml = null) {
        const gallery = this.querySelector('media-gallery');
        const rail = gallery?.querySelector('[id^="GalleryThumbnails-"]');
        const list = rail?.querySelector('.thumbnail-list');
        if (!list) return;

        const findItem = (root) => Array.from(root?.querySelectorAll('li[data-target]') || [])
          .find((item) => item.dataset.target === mediaId);
        let item = findItem(list);

        // Keep existing buttons and their theme click handlers whenever possible.
        // A new item is only necessary if Shopify supplied a previously absent one.
        if (!item && sourceHtml) {
          const incomingGallery = sourceHtml.querySelector('media-gallery');
          const incoming = findItem(incomingGallery?.querySelector('.thumbnail-list'));
          if (incoming) {
            item = incoming.cloneNode(true);
            item.querySelector('button')?.addEventListener('click', (event) => {
              event.preventDefault();
              gallery.setActiveMedia?.(item.dataset.target, false);
            });
            list.appendChild(item);
          }
        }
        if (!item) return;

        // Dawn hides later variant thumbnails. Put the chosen variant in the
        // first slot so the visible thumbnail itself matches the main image.
        // Moving the existing element preserves its click handlers and IDs.
        const isVariantThumbnail = item.classList.contains('thumbnail-list_item--variant') ||
          item.classList.contains('thumbnail-list__item--variant');
        if (isVariantThumbnail && list.firstElementChild !== item) list.prepend(item);
        item.hidden = false;
        item.classList.remove('hidden');

        // The custom underline uses [aria-current], so remove it from old
        // buttons entirely rather than setting aria-current="false".
        list.querySelectorAll('button[aria-current]').forEach((button) => {
          button.removeAttribute('aria-current');
        });
        const button = item.querySelector('button');
        button?.setAttribute('aria-current', 'true');
        const image = button?.querySelector('img');
        if (image) image.loading = 'eager';

        // Refresh the slider's cached item order without replacing the rail;
        // the custom rail's measurements and observers keep the same elements.
        rail.resetPages?.();
        if (isVariantThumbnail) {
          list.scrollTop = 0;
          rail.scrollLeft = 0;
        }
      }

      previewVariantMedia(variant) {
        this._avMediaSequence = (this._avMediaSequence || 0) + 1;
        if (!variant || !this.isConnected) return;
        // Activate the selected media immediately. Mobile browsers must not
        // wait for image decoding before moving away from the previous poster.
        this.warmVariantImage(variant, true);
        const media = this.variantMediaNode(variant);
        if (!media || typeof media.gallery.setActiveMedia !== 'function') return;
        if (media.template) {
          const list = media.gallery.querySelector('ul.product__media-list');
          if (!list) return;
          const clone = media.node.cloneNode(true);
          clone.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; });
          list.appendChild(clone);
        }
        this.syncVariantThumbnail(media.mediaId);
        media.gallery.setActiveMedia(media.mediaId, true);
      }

      async preloadVariantImages() {
        if (!this.isConnected || navigator.connection?.saveData) return;
        const variants = this.parseJsonScript(this.variantSelectors, '[data-artvint-variants]');
        if (!Array.isArray(variants)) return;
        const current = this.getSelectedVariant(this);
        const score = (variant) => variant.options.filter((value, i) => value === current?.options?.[i]).length;
        const seen = new Set();
        const queue = [...variants].sort((a, b) => score(b) - score(a)).filter((variant) => {
          const id = variant.featured_media?.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        }).slice(0, 24);
        const worker = async () => {
          while (this.isConnected && queue.length) await this.warmVariantImage(queue.shift());
        };
        await Promise.all([worker(), worker()]);
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);

          // Make a valid customer selection explicit, including a return to
          // Shopify's original first variant. Never resolve another product
          // against this product's variant list (combined listings).
          if (url === this.dataset.url) {
            const selectedVariant = this.variantForOptionValues(optionValues);
            if (selectedVariant) params.push(`variant=${encodeURIComponent(selectedVariant.id)}`);
          }
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // Synchronize the thumbnail rail for both normal and preloaded updates.
        this.syncVariantThumbnail(`${this.dataset.section}-${variantFeaturedMediaId}`, html);

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
